import React, {useState, useContext} from 'react';
import { ASSETS_WITH_COUNT } from './queries';
import { useMutation } from '@apollo/client';
import { MainContainer, ContentContainer, Input, PageTitle } from '../../elements';
import DataTable from '@preflighttech/preflight-tables';
import { View, Text, Pressable } from 'react-native';
import { Link } from 'app/src/utils/routing';
import Filter from 'app/src/elements/Filter';
import moment from 'moment';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';
import SubNav from '../../elements/SubNav';
import NewAsset from './new';
import { SubNavContainer } from '../../elements';
import Modal from '../../elements/Modal';
import elRed from '../../styles/colors';
import AuthContext from '../../contexts/AuthContext';
import { currency } from 'app/src/components/Sales/Process';

import {
  AssetsTabButtonContainer,
  AssetsTabButton,
  AssetsTabText
} from './elements';

import formattedLocation from './formattedLocation';

export const TITLE_LOG_STATUSES = [
  'Received', 'Transferred', 'Release', 'Problem'
]

const SellerAssets = props => {
  const { sellerId, canAddAsset, user } = useContext(AuthContext);
  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [activeAssetsTab, setActiveAssetsTab] = useState('Unsold');
  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [activeAssetsTab.toLowerCase()],
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  let currentYear = moment().format('YYYY');
  let years = [];
  for(let x = 0; x < 100; x++) {
    years.push(currentYear - x);
  }

  const columns = [
    {
      key: 'vin',
      label: 'VIN',
      sort: 'asc',
      content: (entry) => {
        return (
          <View>
            <Link to={`/assets/${entry.entry.id}`}>
              {`${entry.entry.vin}`}
              <Text style={{color: elRed}}>
                {`${ entry.entry.status === 'Not Yet Ready' ?
                  ' - ' + entry.entry.status : ''
                }`}
              </Text>
            </Link>
          </View>
        )
      }
    },
    {
      key: 'clientNumber',
      label: user?.seller.clientNumberLabel,
    },
    {
      key: 'year',
      label: 'Year',
    },
    {
      key: 'make',
      label: 'Make',
    },
    {
      key: 'model',
      label: 'Model',
    },
    {
      key: 'correctedLotNumber',
      label: 'Lot #',
      sort: 'prevent',
    },
    {
      key: 'cachedTitleStatus',
      label: 'Title Status',
      content: (entry) => {
        return entry.entry.cachedTitleStatus
      }
    },
    {
      key: 'location',
      label: 'Location',
      value: ({ entry }) => formattedLocation(entry),
    },
    {
      key: 'assignedBy',
      label: 'Assigned By',
      hidden: 'Sold' === activeAssetsTab,
      value: ({ entry }) => (
        'SellerUser' === entry.createdByType ? entry.createdByName : ''
      )
    },
    {
      key: 'soldAmount',
      label: 'Sold Price',
      hidden: 'Unsold' === activeAssetsTab,
      content: ({ value }) => currency(value),
    },
    {
      key: 'sellerNet',
      label: 'Net ACH to Seller',
      hidden: 'Unsold' === activeAssetsTab,
      sort: 'prevent',
      value: ({ entry }) => entry.soldSaleAsset?.sellerNet,
      content: ({ value }) => currency(value),
    },
    {
      key: 'uccExpiresOn',
      label: 'Eligible Sale Date',
      content: entry => {
        if(entry.entry.uccExpiresOn) {
          return moment(entry.entry.uccExpiresOn).format('M/D/YYYY')
        }
      }
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: (entry) => {
        return (
          <View style={{display: 'inline-flex'}}>
            <Link
              to={`/assets/${entry.entry.id}`}
              style={{marginLeft: '10px'}}
            >
              View
            </Link>
          </View>
        )
      }
    }
  ]

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px'}}>

          <Text>Vin:</Text>
          <input
            type={'text'}
            name="vin"
            onChange={e => setFilter('vin', e.target.value)}
            value={filters.vin || ''}
          />
          <Text>Year:</Text>
          <select
            type={'select'}
            name="year"
            onChange={e => setFilter('year', e.target.value)}
            value={filters.year || ''}
          >
            <option key={`year-any`}>{'Any'}</option>)
            {years.map(year => <option key={`year-${year}`}>{year}</option>)}
          </select>
          <Text>Make:</Text>
          <input
            type={'text'}
            name="make"
            onChange={e => setFilter('make', e.target.value)}
            value={filters.make || ''}
          />
          <Text>Model:</Text>
          <input
            type={'text'}
            name="model"
            onChange={e => setFilter('model', e.target.value)}
            value={filters.model || ''}
          />
          <Text>Cached Title Status:</Text>
          <select
            type={'select'}
            name='cachedTitleStatus'
            onChange={e => setFilter('cachedTitleStatus', e.target.value)}
            value={filters.cachedTitleStatus || ''}
          >
            <option value="">All</option>
            {TITLE_LOG_STATUSES.map(status => (
              <option key={`status-${status}`} value={status}>
                {status}
              </option>
            ))}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      {getSubNavContents()}
      <ContentContainer>
        <PageTitle>
          <Text><b>All Assets</b></Text>
        </PageTitle>
        <AssetsTabButtonContainer>
          <AssetsTabButton
            onPress={() => setActiveAssetsTab('Unsold')}
            active={'Unsold' === activeAssetsTab}
          >
            <AssetsTabText active={'Unsold' === activeAssetsTab}>
              Unsold Assets
            </AssetsTabText>
          </AssetsTabButton>
          <AssetsTabButton
            onPress={() => setActiveAssetsTab('Sold')}
            active={'Sold' === activeAssetsTab}
          >
            <AssetsTabText active={'Sold' === activeAssetsTab}>
              Sold Assets
            </AssetsTabText>
          </AssetsTabButton>
        </AssetsTabButtonContainer>
        <DataTable.Gql
          setRefetch={setRefetch}
          query={ASSETS_WITH_COUNT}
          queryName='assetsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.assetsWithCount)}
          htmlTable
        />
      </ContentContainer>
    </MainContainer>
  )
  function getSubNavContents() {
    return (
      <SubNavContainer>
        {getLinks()}
      </SubNavContainer>
    )
    function getLinks() {
      return (
        <View style={{display: 'flex', flexDirection: 'row'}}>
          {canAddAsset &&
            <Pressable
              onPress={() => setShowAddAssetModal(true)}
            >
              <Text>+ Add Asset</Text>
            </Pressable>
          }
          {showAddAssetModal &&
            <Modal
              showModal={showAddAssetModal}
              setShowModal={setShowAddAssetModal}
              modalType={'Add Asset'}
            >
              <NewAsset
                setShowModal={setShowAddAssetModal}
                refetch={refetch}

              />
            </Modal>
          }
        </View>
      )
    }
  }
}

export default SellerAssets;
